import React, { useEffect, useState } from 'react';
import { Text, Link, Icon, Shimmer } from '@fluentui/react';
import './ListPanel.scss';
import { GetTypes, keyPress, requestAggregateSubmit } from 'app/components/helpers';
import { formatCustomerSummary } from 'app/modules/newTicketView/customerDisplay';
import { resolveCustomerSummaryText } from 'app/modules/newTicketView/generalFeedback';
import { useIsNewTicketViewEnabled } from 'app/modules/newTicketView/viewFlag';
import useStore, { IStore } from 'app/store';
import { useBoolean } from '@fluentui/react-hooks';
import AddImpact from 'app/components/addCustomer/AddCustomer';
import AddCustomerSurface from 'app/components/addCustomer/AddCustomerSurface';
import AddCustomerSuccessToast from 'app/components/addCustomer/AddCustomerSuccessToast/AddCustomerSuccessToast';
import { resolveCustomerImpactSuccess } from 'app/components/addCustomer/customerImpactSuccess';
import { Trans } from 'react-i18next';
import { usePostRequestType } from 'app/services/mutations';
import { useAssociateRequests } from 'app/services/request-api';
import { EnumLookUp, IAssociateRequestsDto, RequestDto, PaginatedListOfRequestDto, RequestListBuilderFilter, UserDto, CustomerImpactDto, IRequestDto } from 'generated/request-backend';
import { AppQueryClient } from 'app/services/clients/query-client';
import { StringConstants } from 'app/utils/constants';
import { UserDomain } from 'app/enums/UserDomain.enum';
import { CustomerImpactResponseDto } from 'generated/crew-backend'; 
import { logFlowEvent } from 'app/modules/user/createTicket/flowTelemetry';
import { ViewTelemetryConstants, VIEW_TELEMETRY_SECTION } from 'app/modules/newTicketView/telemetryConstants';

interface IProps {
    id: string;
    data: PaginatedListOfRequestDto;
    requestData: RequestDto[];
    selectedRequest: RequestDto;
    requestTypes: EnumLookUp[];
    teams: EnumLookUp[];

    setSelectedRequest: Function;

    setRequestData: Function;
    setIsAddCustomerModalOpen: Function;
    setRequestFilter: Function;
    cardTypeFilter: number;
    isLoadingRequestList: boolean;
    isListBuilderLoading: boolean;
    listBuilderResults: PaginatedListOfRequestDto;
    previousLocation: string;
    listBuilderFilters: RequestListBuilderFilter;
}

const ListPanel: React.FC<IProps> = (props) => {
    const isNewTicketViewEnabled = useIsNewTicketViewEnabled();
    // props variables
    const { data, selectedRequest, requestData, isLoadingRequestList, listBuilderResults, previousLocation, isListBuilderLoading, listBuilderFilters } = props;
    // props functions
    const { setSelectedRequest, setRequestData } = props;

    // state variables
    const [isMinimizedView, setIsMinimizedView] = useState(true);
    const userDetails: UserDto = useStore((state: IStore) => state.userDetails as UserDto);

    // helper functions
    const handleToggleView = (state: boolean) => {
        setIsMinimizedView(state);
    };

    // hooks
    useEffect(() => {
        if (listBuilderFilters !== null && previousLocation === '/manage-lists' && !isListBuilderLoading && listBuilderResults !== undefined) {
            setRequestData(listBuilderResults?.items?.filter((item: RequestDto) => item.isParent !== true));
            setSelectedRequest(listBuilderResults?.items?.filter((item: RequestDto) => item.isParent !== true)[0])
        } else if (!isLoadingRequestList) {
            if (data?.items && data?.items.length > 0) {
                setRequestData(data?.items);
                if (selectedRequest?.unifiedTrackingId === undefined) {
                    setSelectedRequest(data?.items[0]);
                }
            } else {
                setRequestData([]);
            }
        }
    }, [data, setRequestData, isLoadingRequestList, listBuilderResults, previousLocation, isListBuilderLoading, listBuilderFilters, selectedRequest]);

    //add customer
    const addSuccessPopUp = useStore((state: IStore) => state.addSuccessPopUp);
    const [isModalOpen, { setTrue: showModal, setFalse: hideModal }] = useBoolean(false);
    const { mutate: associateRequests } = useAssociateRequests();
    const { mutate: postRequestTypeHelp } = usePostRequestType();

    // State variables for addCustomer
    const [requestProcessing, setRequestProcessing] = useState(false);
    const [displayScreen, setDisplayScreen] = useState('searchCust');
    const [toastCustomerName, setToastCustomerName] = useState('');
    const [showAddCustomerToast, setShowAddCustomerToast] = useState(false);

    useEffect(() => {
        if (displayScreen === 'successPopUp') {
            hideModal();
            setDisplayScreen('searchCust');
            setShowAddCustomerToast(true);
        }
    }, [displayScreen, hideModal]);

    const handleAddCustomerSuccess = (postAddCustomerResponseData: CustomerImpactResponseDto, addedCustomerImpacts: CustomerImpactDto[]) => {
        const successResolution = resolveCustomerImpactSuccess(
            postAddCustomerResponseData,
            selectedRequest.unifiedTrackingId
        );
        setToastCustomerName(addedCustomerImpacts[0]?.customerName || 'Customer');
        logFlowEvent(ViewTelemetryConstants.CUSTOMER_ADDED, VIEW_TELEMETRY_SECTION, {
            role: userDetails.email === selectedRequest.createdBy?.email ? 'creator' : 'viewer',
            requestType: selectedRequest.requestType,
            customerCount: addedCustomerImpacts.length,
            parentCreated: successResolution.parentCreated
        });
        if (!successResolution.matchesSelectedRequest) {
            setRequestProcessing(false);
            setDisplayScreen('errorPopUp');
            return;
        }

        if (successResolution.isStructuredFamilyResponse) {
            setRequestProcessing(false);
            setDisplayScreen('successPopUp');
            AppQueryClient.invalidateQueries('getRequestById');
            AppQueryClient.invalidateQueries('useRequestList');
            AppQueryClient.invalidateQueries(StringConstants.REQUEST_QUERY);
            return;
        }

        if (selectedRequest.parentRequestId !== "" && selectedRequest.parentRequestId !== null) {
            const associationObject : IAssociateRequestsDto = {
                requestIds: [postAddCustomerResponseData?.id],
                associatedRequestId: selectedRequest.parentRequestId,
                expectedCustomerFamilySubmissionId: selectedRequest.customerFamilySubmissionId,
                expectedCustomerFamilyGeneration: selectedRequest.customerFamilyGeneration
            };

            associateRequests(associationObject, {
                onSuccess: () => {
                    setRequestProcessing(false);
                    setDisplayScreen('successPopUp');
    
                    AppQueryClient.invalidateQueries('getRequestById');
                    AppQueryClient.invalidateQueries('useRequestList');
                    AppQueryClient.invalidateQueries('requestQuery');
                },
                onError: () => {
                    setRequestProcessing(false);
                    setDisplayScreen('errorPopUp')
                }
            });
        } else if (selectedRequest.unifiedTrackingId !== postAddCustomerResponseData?.id) {
            // aggregate parent with selectedRequest becoming aggregate parent
            let datapass: IRequestDto = {
                summary: selectedRequest.summary,
                description: selectedRequest.description,

                requestedResolutionDate: selectedRequest.requestedResolutionDate,
                whyThisDateIsImportant: selectedRequest.whyThisDateIsImportant,

                status: 1,
                attachments: selectedRequest.attachments,
                customerImpacts: [] as CustomerImpactDto[],
                isParent: true
            };

            var aggregateCustomerImpact: CustomerImpactDto[] = [];

            selectedRequest.customerImpacts.forEach((impact) => {
                aggregateCustomerImpact.push(impact);
            });

            datapass.customerImpacts = aggregateCustomerImpact;
            datapass.status = selectedRequest.status;

            const requestAggregateData = requestAggregateSubmit(selectedRequest.requestType, selectedRequest, datapass);
            postRequestTypeHelp(
                { subUrl: requestAggregateData.subUrl, data: requestAggregateData.data },
                {
                    onSuccess: (response) => {
                        finishParentAssociation(response, postAddCustomerResponseData?.id);
                    }
                }
            );
        } else {
            setRequestProcessing(false);
            setDisplayScreen('successPopUp');

            AppQueryClient.invalidateQueries(StringConstants.REQUEST_QUERY);
        }
    };

    const finishParentAssociation = (parentId: string, postAddCustomerId: string) => {
        const associationObject : IAssociateRequestsDto = {
            requestIds: [selectedRequest.unifiedTrackingId, postAddCustomerId],
            associatedRequestId: parentId,
            expectedCustomerFamilySubmissionId: selectedRequest.customerFamilySubmissionId,
            expectedCustomerFamilyGeneration: selectedRequest.customerFamilyGeneration
        };

        associateRequests(associationObject, {
            onSuccess: () => {
                setRequestProcessing(false);
                setDisplayScreen('successPopUp');

                AppQueryClient.invalidateQueries('getRequestById');
                AppQueryClient.invalidateQueries('useRequestList');
                AppQueryClient.invalidateQueries('requestQuery');
            },
            onError: () => {
                setRequestProcessing(false);
                setDisplayScreen('errorPopUp')
            }
        });
    };

    return (
        <div className={`list-panel-container ${isMinimizedView ? 'minimized-view' : 'maximized-view'}`} role="main" aria-label="List view panel" data-view={isMinimizedView ? 'minimized' : 'maximized'}>
            <div className="list-view-toggle">
                <Text className={`${isMinimizedView ? 'active' : ''}`}>
                    <Trans>Minimized View</Trans>
                </Text>
                <div className="lv-toggle-wrap">
                    <div
                        className={`lv-toggle ${isMinimizedView ? 'handle-left' : ''}`}
                        onClick={() => handleToggleView(true)}
                        onKeyPress={(e) => keyPress(e, () => handleToggleView(true))}
                        tabIndex={0}
                        aria-label={`${isMinimizedView ? 'selected' : ''} minimized view`}
                        role='button'
                        data-testid="toggle-test">
                        <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path d="M3 17H15C15.5523 17 16 17.4477 16 18C16 18.5128 15.614 18.9355 15.1166 18.9933L15 19H3C2.44772 19 2 18.5523 2 18C2 17.4872 2.38604 17.0645 2.88338 17.0067L3 17H15H3ZM3 11H21C21.5523 11 22 11.4477 22 12C22 12.5128 21.614 12.9355 21.1166 12.9933L21 13H3C2.44772 13 2 12.5523 2 12C2 11.4872 2.38604 11.0645 2.88338 11.0067L3 11H21H3ZM3 5H18C18.5523 5 19 5.44772 19 6C19 6.51284 18.614 6.93551 18.1166 6.99327L18 7H3C2.44772 7 2 6.55228 2 6C2 5.48716 2.38604 5.06449 2.88338 5.00673L3 5H18H3Z" />
                        </svg>
                    </div>
                    <div
                        className={`lv-toggle ${!isMinimizedView ? 'handle-right' : ''}`}
                        onClick={() => handleToggleView(false)}
                        onKeyPress={(e) => keyPress(e, () => handleToggleView(false))}
                        tabIndex={0}
                        aria-label={`${!isMinimizedView ? 'selected' : ''} maximized view`}
                        role='button'
                        data-testid="togglefalse-test">
                        <svg viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
                            <path d="M6.5 7H1.5L1 6.5V1.5L1.5 1H6.5L7 1.5V6.5L6.5 7ZM2 6H6V2H2V6Z" />
                            <path d="M13.5 7H8.5L8 6.5V1.5L8.5 1H13.5L14 1.5V6.5L13.5 7ZM9 6H13V2H9V6Z" />
                            <path d="M6.5 14H1.5L1 13.5V8.5L1.5 8H6.5L7 8.5V13.5L6.5 14ZM2 13H6V9H2V13Z" />
                            <path d="M13.5 14H8.5L8 13.5V8.5L8.5 8H13.5L14 8.5V13.5L13.5 14ZM9 13H13V9H9V13Z" />
                        </svg>
                    </div>
                </div>
                <Text className={`${!isMinimizedView ? 'active' : ''}`}>
                    <Trans>Maximized View</Trans>
                </Text>
            </div>

            <div id="list-scroll-panel" className="scroll-panel">
                <div className="list-item-wrapper">
                    {requestData && requestData.length > 0 && (
                        <div>
                            {requestData.map((item: RequestDto, index) => (
                                <div
                                    key={index}
                                    className={`list-item ${
                                        item?.unifiedTrackingId === selectedRequest?.unifiedTrackingId ? 'active' : ''
                                    }`}
                                    tabIndex={0}
                                    onClick={() => {
                                        setSelectedRequest(item);
                                    }}
                                    onKeyPress={(e) =>
                                        keyPress(e, () => {
                                            setSelectedRequest(item);
                                        })
                                    }
                                    data-testid="select-test">
                                    <div className="list-title">
                                        <Text>
                                            {item.unifiedTrackingId}
                                            {item.isBookmarked ? (
                                                <Icon iconName="FavoriteStarFill" className="icon star-icon m-l-5" />
                                            ) : (
                                                ''
                                            )}
                                        </Text>

                                        <Link
                                            tabIndex={0}
                                            className="link-list-add"
                                            onKeyPress={(e) =>
                                                keyPress(e, () => {
                                                    addSuccessPopUp(false);
                                                    setShowAddCustomerToast(false);
                                                    showModal();
                                                })
                                            }
                                            onClick={() => {
                                                addSuccessPopUp(false);
                                                setShowAddCustomerToast(false);
                                                showModal();
                                            }}>
                                            +<Trans>Add a customer</Trans>
                                        </Link>
                                    </div>
                                    <div className={`list-desc ${isMinimizedView ? 'text-normal' : ''}`}>
                                        <Text className="title-field">{item.summary}</Text>
                                    </div>
                                    {!isMinimizedView && (
                                        <>
                                            {userDetails.email === item?.createdBy?.email && (
                                                <>
                                                    <div className="list-head">
                                                        <Text>
                                                            <Trans>CUSTOMER</Trans>
                                                        </Text>
                                                    </div>
                                                    <div className="list-value" data-testid="list-customer-value">
                                                        <Text>
                                                            {isNewTicketViewEnabled
                                                                ? resolveCustomerSummaryText(item, { fallback: '--' })
                                                                : item.customerImpacts?.length > 0
                                                                    ? formatCustomerSummary(
                                                                          item.customerImpacts.map((customer) => ({
                                                                              accountName: customer.customerName
                                                                          }))
                                                                      )
                                                                    : '--'}
                                                        </Text>
                                                    </div>
                                                </>
                                            )}
                                            <div className="list-head">
                                                <Text>
                                                    <Trans>REQUEST TYPE</Trans>
                                                </Text>
                                            </div>
                                            <div className="list-value">
                                                <Text>
                                                    { `${GetTypes(item.requestType)} ${UserDomain[item.requestTeam as number]}` }
                                                </Text>
                                            </div>
                                        </>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                    {isLoadingRequestList && (
                        <>
                            <Shimmer />
                            <Shimmer width="75%" />
                            <Shimmer width="50%" />
                        </>
                    )}
                    {!isLoadingRequestList && requestData && requestData.length === 0 && (
                        <div className="list-item">
                            <Trans>No data to display</Trans>
                        </div>
                    )}
                </div>
            </div>

            {hideModal && (
                <AddCustomerSurface
                    testId="list-add-customer"
                    isOpen={isModalOpen}
                    onDismiss={hideModal}
                    useDrawer={isNewTicketViewEnabled}
                    enableEventBubbling>
                    <AddImpact
                        selectedRequest={selectedRequest}
                        onDismiss={hideModal}
                        globalSelectedRequest={selectedRequest}
                        setSelectedRequest={setSelectedRequest}
                        onSuccessHandler={handleAddCustomerSuccess}
                        requestProcessing={requestProcessing}
                        setRequestProcessing={setRequestProcessing}
                        displayScreen={displayScreen}
                        setDisplayScreen={setDisplayScreen}
                    />
                </AddCustomerSurface>
            )}
            {showAddCustomerToast && (
                <AddCustomerSuccessToast
                    customerName={toastCustomerName}
                    onDismiss={() => setShowAddCustomerToast(false)}
                />
            )}
        </div>
    );
};

export default ListPanel;
